import { range } from 'lodash-es';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TableVirtuoso } from 'react-virtuoso';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox/Checkbox';
import { Switch } from 'src/core/interim/Switch';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { SimpleTable, Stack, TooltipPopover } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FieldError } from 'src/modals/common/Purchase';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { Feature, SplitListingInput } from 'src/WebApiController';

import { ModalBodyDataContainer } from '../Modal/Modal.styled';
import * as styles from './SplitListing.css';
import {
  MainLabel,
  SoldTicketContainer,
  SplitListQuickOptionsContainer,
} from './SplitListing.styled';

export const SplitListingBody = () => {
  const { watch, setValue, formState, clearErrors } =
    useFormContext<SplitListingInput>();
  const numOfListings = watch('numOfListings');
  const ticketsByListingNumber = watch('ticketsByListingNumber');
  const retainSeatTraits = watch('retainSeatTraits');

  const hasSplitListingWithSeatTraitsRetainedFeature = useUserHasFeature(
    Feature.SplitListingWithSeatTraitsRetained
  );

  const [allowManualSplitSoldTickets, setAllowManualSplitSoldTickets] =
    useState(false);

  const regenerateListingTicketIds = (numOfListings: number) => {
    // for every ticket who's list index is greater than numOfListings, set it to numOfListings
    const newListingsTickets = ticketsByListingNumber.map((t) => {
      if (t.listingNumber > numOfListings) {
        t.listingNumber = numOfListings;
      }

      return t;
    });

    clearErrors('numOfListings');
    setValue('numOfListings', numOfListings);
    setValue('ticketsByListingNumber', newListingsTickets);
  };

  const assignListingNumberToTicket = useCallback(
    (newListingNum: number, ticketIndex: number) => {
      ticketsByListingNumber[ticketIndex].listingNumber = newListingNum;

      // To keep all the contiguous tickets in the same listing
      // All tickets above ticketIndex but listing-num greater than it has to be set to the same num
      for (let i = ticketIndex - 1; i >= 0; i--) {
        if (ticketsByListingNumber[i].listingNumber > newListingNum) {
          ticketsByListingNumber[i].listingNumber = newListingNum;
        }
      }
      // And all tickets below ti but listing-num less than it also has to be set to the same num
      for (let i = ticketIndex + 1; i < ticketsByListingNumber.length; i++) {
        if (ticketsByListingNumber[i].listingNumber < newListingNum) {
          ticketsByListingNumber[i].listingNumber = newListingNum;
        }
      }

      clearErrors('ticketsByListingNumber');
      setValue('ticketsByListingNumber', [...ticketsByListingNumber]);
    },
    [clearErrors, setValue, ticketsByListingNumber]
  );

  return (
    <ModalBodyDataContainer>
      <MainLabel>
        <Content id={ContentId.SplitListing} />
      </MainLabel>
      <SplitListQuickOptionsContainer>
        <div>
          <Content id={ContentId.NumOfListingsToCreate} />:
          <div className={styles.numOfListingContainer}>
            <PosTextField
              type="number"
              inputMode="numeric"
              value={numOfListings}
              onChange={(e) => {
                const newNum = parseInt(e.target.value);
                if (newNum !== numOfListings) {
                  regenerateListingTicketIds(
                    Math.min(Math.max(2, newNum), ticketsByListingNumber.length)
                  );
                }
              }}
              data-testid="split-num-listings"
            />
          </div>
        </div>
        <div>
          <Content id={ContentId.IgnoreSoldTickets} />{' '}
          <TooltipPopover
            variant="textPlain"
            textColor="primary"
            triggerContent={
              <InfoOutlineIcon
                withHoverEffect
                size={vars.iconSize.s}
                fill={vars.color.textBrandActive}
              />
            }
          >
            <Stack direction="column">
              <span>
                <Content id={ContentId.SplitListing_AllowSplitSoldTickets} />
              </span>
              <span>
                <Content
                  id={ContentId.SplitListing_DoNotAllowSplittingSoldTickets}
                />
              </span>
            </Stack>
          </TooltipPopover>
          :
          <Switch
            checked={allowManualSplitSoldTickets}
            onChange={(value) => {
              if (value.target.checked !== allowManualSplitSoldTickets) {
                setAllowManualSplitSoldTickets(value.target.checked);
              }
            }}
          />
          {formState.errors.numOfListings?.message && (
            <FieldError>{formState.errors.numOfListings?.message}</FieldError>
          )}
        </div>
      </SplitListQuickOptionsContainer>
      <div className={styles.tableContainerVirtuoso}>
        <TableVirtuoso
          data={ticketsByListingNumber}
          style={{ width: '100%', height: '100%' }}
          overscan={{ main: 10, reverse: 10 }}
          components={{
            Table: SimpleTable.Table,
            TableHead: SimpleTable.Thead,
            TableBody: SimpleTable.Tbody,
            TableRow: SimpleTable.Tr,
          }}
          fixedHeaderContent={() => (
            <SimpleTable.Tr
              style={{ backgroundColor: vars.color.backgroundPrimary }}
            >
              <SimpleTable.Th>
                <Content id={ContentId.Ticket} />
              </SimpleTable.Th>
              <SimpleTable.Th>
                <Content id={ContentId.Seat} />
              </SimpleTable.Th>
              <SimpleTable.Th>
                <Content id={ContentId.UnitCost} />
              </SimpleTable.Th>
              <SimpleTable.Th>
                <Content id={ContentId.Listing} /> #
              </SimpleTable.Th>
            </SimpleTable.Tr>
          )}
          itemContent={(ti, t) => (
            <>
              <SimpleTable.Td
                title={`${t.ticket.tgId}-${t.ticket.ord}`}
                className={styles.tableCell}
              >
                <FormatContent
                  id={FormatContentId.SeatNumber}
                  params={(ti + 1).toString()}
                />
              </SimpleTable.Td>
              <SimpleTable.Td className={styles.tableCell}>
                {t.ticket.seat}
              </SimpleTable.Td>
              <SimpleTable.Td className={styles.tableCell}>
                {t.ticket.unitCost?.disp}
              </SimpleTable.Td>
              <SimpleTable.Td className={styles.tableCell}>
                {t.ticket.saleId && !allowManualSplitSoldTickets ? (
                  <SoldTicketContainer title={t.listingNumber.toString()}>
                    <Content id={ContentId.Sold} />
                  </SoldTicketContainer>
                ) : (
                  <PosSelect
                    value={t.listingNumber.toString()}
                    size="md"
                    contentProps={{ align: 'end' }}
                    onChange={(newListingNumber: string | null) => {
                      if (newListingNumber) {
                        const newListingNum = parseInt(newListingNumber);
                        assignListingNumberToTicket(newListingNum, ti);
                      }
                    }}
                    valueOptionsContent={range(numOfListings).reduce(
                      (r, i) => {
                        r[(i + 1).toString()] = (i + 1).toString();
                        return r;
                      },
                      {} as Record<string, string>
                    )}
                    data-testid={`split-seat-listing-${ti}`}
                  />
                )}
              </SimpleTable.Td>
            </>
          )}
        />
      </div>
      {hasSplitListingWithSeatTraitsRetainedFeature && (
        <div className={styles.tableCell}>
          <Checkbox
            checked={retainSeatTraits}
            onChange={(e) => setValue('retainSeatTraits', e.target.checked)}
            label={<Content id={ContentId.SplitListing_RetainSeatTraits} />}
            labelPosition="right"
            data-testid="retain-seat-traits-checkbox"
          />
        </div>
      )}
    </ModalBodyDataContainer>
  );
};
